import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Menu,
  TextField,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

interface ExcelStyleFilterOption {
  value: string;
  label: string;
  group?: string;
  /** מפתחות נוספים (למשל כמה ערכי enum) שמיוצגים תחת אותה שורת פילטר ונבחרים/מתבטלים יחד */
  values?: string[];
}

interface ExcelStyleFilterProps {
  label: string;
  options: ExcelStyleFilterOption[];
  selected: Set<string>;
  onChange: (selected: Set<string>) => void;
}

const ExcelStyleFilter: React.FC<ExcelStyleFilterProps> = ({ label, options, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [search, setSearch] = useState("");
  const open = Boolean(anchorEl);

  const filteredOptions = useMemo(
    () => options.filter((opt) => opt.label.includes(search)),
    [options, search]
  );

  const groupedOptions = useMemo(() => {
    const groups: { group: string | undefined; options: ExcelStyleFilterOption[] }[] = [];
    filteredOptions.forEach((opt) => {
      const last = groups[groups.length - 1];
      if (last && last.group === opt.group) {
        last.options.push(opt);
      } else {
        groups.push({ group: opt.group, options: [opt] });
      }
    });
    return groups;
  }, [filteredOptions]);

  const optionKeys = (opt: ExcelStyleFilterOption) => opt.values ?? [opt.value];
  const isOptionSelected = (opt: ExcelStyleFilterOption) => optionKeys(opt).every((key) => selected.has(key));

  const allSelected = options.length > 0 && options.every(isOptionSelected);
  const isFiltered = options.length > 0 && !allSelected;
  // הספירה היא לפי שורות פילטר כפי שהמשתמש רואה אותן, לא לפי הערכים הגולמיים שכל שורה מייצגת
  const selectedOptionCount = useMemo(() => options.filter(isOptionSelected).length, [options, selected]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch("");
  };

  const toggleOption = (opt: ExcelStyleFilterOption) => {
    const next = new Set(selected);
    const keys = optionKeys(opt);
    const isSelected = keys.every((key) => next.has(key));
    keys.forEach((key) => (isSelected ? next.delete(key) : next.add(key)));
    onChange(next);
  };

  const selectAll = () => {
    onChange(new Set(options.flatMap(optionKeys)));
  };

  const clearAll = () => {
    onChange(new Set());
  };

  return (
    <Box>
      <Button
        variant="outlined"
        size="small"
        onClick={handleOpen}
        startIcon={<FilterListIcon fontSize="small" />}
        endIcon={<ArrowDropDownIcon fontSize="small" />}
        sx={{
          textTransform: "none",
          borderColor: isFiltered ? "primary.main" : undefined,
          color: isFiltered ? "primary.main" : undefined,
          fontWeight: isFiltered ? 700 : 400,
        }}
      >
        {label}
        {isFiltered && ` (${selectedOptionCount}/${options.length})`}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { dir: "rtl", sx: { maxWidth: 280 } } }}
      >
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5, width: 250, maxWidth: "100%" }}>
          <TextField
            size="small"
            fullWidth
            placeholder="חיפוש..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", px: 1.5, py: 0.5 }}>
          <Button size="small" onClick={selectAll} sx={{ textTransform: "none" }}>
            בחר הכל
          </Button>
          <Button size="small" onClick={clearAll} sx={{ textTransform: "none" }}>
            נקה הכל
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 300, overflowY: "auto", overflowX: "hidden", px: 1.5, py: 0.5 }}>
          {filteredOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ px: 1, py: 1 }}>
              אין תוצאות
            </Typography>
          ) : (
            groupedOptions.map((group, groupIndex) => (
              <Box key={group.group ?? `_${groupIndex}`}>
                {group.group && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", px: 1, pt: groupIndex > 0 ? 1 : 0, color: "text.secondary", fontWeight: 700 }}
                  >
                    {group.group}
                  </Typography>
                )}
                {group.options.map((opt) => (
                  <Box
                    key={opt.value}
                    onClick={() => toggleOption(opt)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      cursor: "pointer",
                      borderRadius: 1,
                      "&:hover": { backgroundColor: "action.hover" },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isOptionSelected(opt)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOption(opt)}
                    />
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: "normal", wordBreak: "break-word" }}
                    >
                      {opt.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))
          )}
        </Box>
      </Menu>
    </Box>
  );
};

export default ExcelStyleFilter;
